import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, ShoppingBag, User, Menu, X } from "lucide-react";
import { useCjCart } from "../context/CjCartContext";
import { useContent } from "../hooks/useContent";
import { MegaMenuDesktop, MegaMenuMobile } from "./MegaMenu";

const NAV = [
  { label: "Home", to: "/" },
  { label: "Shop", to: "/shop" },
  { label: "Live", to: "/live" },
  { label: "Memberships", to: "/membership" },
  { label: "Build Your Store", to: "/become-a-seller" },
  { label: "My Membership", to: "/my-membership" },
  { label: "Seller Dashboard", to: "/seller-dashboard" },
];

const SearchBox = ({ mobile }) => {
  const [q, setQ] = useState("");
  const navigate = useNavigate();
  const submit = (e) => { e.preventDefault(); navigate(`/shop?q=${encodeURIComponent(q)}`); };
  return (
    <form onSubmit={submit} className={mobile ? "flex w-full bg-taven-gray rounded-full h-11 px-4 items-center" : "hidden md:flex flex-1 max-w-xl mx-8 bg-taven-gray border border-transparent focus-within:border-black/20 rounded-full h-11 px-4 items-center"}>
      <Search className="w-4 h-4 text-muted-foreground shrink-0" />
      <input data-testid="search-input" value={q} onChange={(e) => setQ(e.target.value)}
        placeholder="Search products, brands, categories..."
        className="bg-transparent outline-none px-3 w-full text-sm" />
      <button data-testid="search-submit" type="submit" className="text-xs font-bold uppercase tracking-wider text-taven-gold">Go</button>
    </form>
  );
};

export const Header = () => {
  const [sticky, setSticky] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { count } = useCjCart();
  const bar = useContent("announcement_bar", { text: "Build. Earn. Grow. Leave a Legacy.", tail: "Explore TAVEN memberships • Big Sales live now", enabled: true });

  useEffect(() => {
    const onScroll = () => setSticky(window.scrollY > 10);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      {bar.enabled !== false && (
        <div className="bg-taven-black text-white text-center text-[11px] md:text-xs py-2 font-medium tracking-wide" data-testid="announcement-bar">
          <span className="text-taven-gold font-bold">{bar.text}</span>{bar.tail ? ` — ${bar.tail}` : ""}
        </div>
      )}

      <header className={`sticky top-0 z-50 bg-white/95 backdrop-blur-xl border-b transition-shadow ${sticky ? "shadow-md border-black/10" : "border-black/5"}`} data-testid="site-header">
        <div className="flex items-center justify-between h-16 md:h-20 px-4 md:px-8 max-w-7xl mx-auto">
          <div className="flex items-center gap-3">
            <button data-testid="mobile-menu-toggle" className="lg:hidden p-1" onClick={() => setMenuOpen(true)} aria-label="Open menu"><Menu className="w-6 h-6" /></button>
            <Link to="/" data-testid="logo-link" className="flex flex-col leading-none">
              <span className="text-xl md:text-2xl font-black font-display tracking-tighter text-black">TAVEN</span>
              <span className="text-[8px] md:text-[9px] font-bold uppercase tracking-[0.3em] text-taven-gold">System LLC</span>
            </Link>
          </div>

          <SearchBox />

          <div className="flex items-center gap-3 md:gap-5">
            <Link to="/account" data-testid="header-account" className="p-1 hover:text-taven-gold" aria-label="My Account"><User className="w-5 h-5 md:w-6 md:h-6" /></Link>
            <Link to="/market/cart" data-testid="header-cart" className="relative p-1 hover:text-taven-gold" aria-label="Cart">
              <ShoppingBag className="w-5 h-5 md:w-6 md:h-6" />
              {count > 0 && <span data-testid="cart-count" className="absolute -top-1.5 -right-1.5 bg-taven-orange text-white text-[10px] font-bold min-w-[18px] h-[18px] flex items-center justify-center rounded-full px-1">{count}</span>}
            </Link>
          </div>
        </div>

        <nav className="hidden lg:block border-t border-black/5" data-testid="primary-nav">
          <div className="max-w-7xl mx-auto px-8 flex items-center gap-7 h-11">
            {NAV.map((n) => (
              <Link key={n.to} to={n.to} data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="text-xs font-bold uppercase tracking-wider hover:text-taven-gold whitespace-nowrap">{n.label}</Link>
            ))}
          </div>
        </nav>
        <MegaMenuDesktop />
      </header>

      {menuOpen && (
        <div className="fixed inset-0 z-[60] lg:hidden" data-testid="mobile-menu">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMenuOpen(false)} />
          <div className="absolute left-0 top-0 h-full w-[85%] max-w-sm bg-white p-6 overflow-y-auto" style={{ animation: "fade-up 0.3s ease" }}>
            <div className="flex items-center justify-between mb-6">
              <span className="text-xl font-black font-display">TAVEN</span>
              <button onClick={() => setMenuOpen(false)} data-testid="mobile-menu-close"><X className="w-6 h-6" /></button>
            </div>
            <SearchBox mobile />
            <div className="mt-6 flex flex-col divide-y divide-black/5">
              {NAV.map((n) => (
                <Link key={n.to} to={n.to} onClick={() => setMenuOpen(false)}
                  data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="py-3 text-sm font-semibold uppercase tracking-wider">{n.label}</Link>
              ))}
            </div>
            <p className="mt-6 mb-1 text-taven-gold text-[11px] font-bold uppercase tracking-[0.25em]">Shop by Department</p>
            <MegaMenuMobile onNavigate={() => setMenuOpen(false)} />
          </div>
        </div>
      )}
    </>
  );
};

export default Header;
